import { ContentCopy, Refresh, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useFetch } from '../../../hooks/useFecth';
import ListPageLayout from '../../../components/ListPageLayout';
import ConfirmationModal from '../../../components/ConfirmationModal';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';
import { useAuth } from '../../../context/AuthContext';
import DialogInviteUser from './components/DialogInviteUser';
import DialogChangeUserTeam from './components/DialogChangeUserTeam';
import { adminTeamLabel, EUROBEM_ADMIN_TEAMS } from './adminTeams';

function resolveInviteUrl(invite) {
    if (invite?.inviteUrl) return invite.inviteUrl;
    if (!invite?.token || typeof window === 'undefined') return '';
    return `${window.location.origin}/convite/${encodeURIComponent(invite.token)}`;
}

function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('pt-BR');
}

export default function User() {
    const { user } = useAuth();
    const { setErrors, enqueueSnackbar } = useErrors();
    const [tab, setTab] = useState('users');
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [revokeInvite, setRevokeInvite] = useState(null);
    const [deleteUser, setDeleteUser] = useState(null);

    const { data, mutate, isFetching } = useFetch('users', { params: { per_page: 100, page: 1 } });
    const { data: invites, mutate: mutateInvites, isFetching: isFetchingInvites } = useFetch(
        'users/invites',
        { params: { status: 'pending' } },
    );

    const rows = data?.rows ?? [];
    const pendingInvites = Array.isArray(invites) ? invites : invites?.data ?? [];

    const groupedUsers = useMemo(
        () =>
            EUROBEM_ADMIN_TEAMS.map(group => ({
                ...group,
                users: rows.filter(item => item.team === group.value),
            })).filter(group => group.users.length > 0),
        [rows],
    );

    const handleCopyInviteLink = async invite => {
        const url = resolveInviteUrl(invite);
        if (!url) {
            enqueueSnackbar('Link do convite indisponível.', { variant: 'error' });
            return;
        }
        try {
            await navigator.clipboard.writeText(url);
            enqueueSnackbar('Link do convite copiado.');
        } catch (error) {
            setErrors(error);
        }
    };

    const handleResendInvite = async invite => {
        try {
            await server.post(`users/invites/${invite.id}/resend`);
            enqueueSnackbar('Convite reenviado.', { variant: 'success' });
            await mutateInvites();
        } catch (error) {
            setErrors(error);
        }
    };

    const handleRevokeConfirm = async () => {
        if (!revokeInvite) return;
        try {
            await server.delete(`users/invites/${revokeInvite.id}`);
            enqueueSnackbar('Convite revogado.', { variant: 'success' });
            await mutateInvites();
        } catch (error) {
            setErrors(error);
        } finally {
            setRevokeInvite(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteUser) return;
        try {
            await server.delete(`users/${deleteUser.id}`);
            enqueueSnackbar(`Usuário ${deleteUser.userName} excluído.`, { variant: 'success' });
            await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setDeleteUser(null);
        }
    };

    return (
        <>
            <ListPageLayout
                title="Usuários"
                subtitle="Equipe Eurobem. Convide por e-mail; cada pessoa completa o cadastro no link."
                counts={[
                    { label: 'usuários', value: rows.length, color: 'primary' },
                    { label: 'convites', value: pendingInvites.length, color: 'default' },
                ]}
                loading={(isFetching && !data) || (isFetchingInvites && !invites)}
                showViewToggle={false}
                isEmpty={false}
                actions={
                    <Button size="small" variant="contained" onClick={() => setInviteOpen(true)}>
                        Convidar usuário
                    </Button>
                }
            >
                <Tabs
                    value={tab}
                    onChange={(_, next) => setTab(next)}
                    sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab value="users" label="Equipe" />
                    <Tab value="invites" label={`Convites (${pendingInvites.length})`} />
                </Tabs>

                {tab === 'users' ? (
                    rows.length === 0 && !isFetching ? (
                        <Box px={2} py={6}>
                            <Typography color="text.secondary" textAlign="center">
                                Nenhum usuário na equipe Eurobem.
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={3} p={2}>
                            {groupedUsers.map(group => (
                                <Box key={group.value}>
                                    <Stack direction="row" alignItems="center" gap={1} mb={1}>
                                        <Typography variant="subtitle2">{group.label}</Typography>
                                        <Chip size="small" label={group.users.length} />
                                    </Stack>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>E-mail</TableCell>
                                                    <TableCell>Desde</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell align="right" width={96} />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {group.users.map(item => {
                                                    const isSelf = Number(item.id) === Number(user?.id);
                                                    return (
                                                        <TableRow key={item.id} hover>
                                                            <TableCell>{item.userName}</TableCell>
                                                            <TableCell>
                                                                {formatDate(item.createdAt)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    color={item.status ? 'success' : 'default'}
                                                                    variant="outlined"
                                                                    label={item.status ? 'Ativo' : 'Inativo'}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <IconButton
                                                                    size="small"
                                                                    title={
                                                                        isSelf
                                                                            ? 'Você não pode alterar o próprio cargo'
                                                                            : 'Alterar cargo'
                                                                    }
                                                                    disabled={isSelf}
                                                                    onClick={() => setEditUser(item)}
                                                                >
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    title={
                                                                        isSelf
                                                                            ? 'Você não pode excluir a própria conta'
                                                                            : 'Excluir'
                                                                    }
                                                                    disabled={isSelf}
                                                                    onClick={() => setDeleteUser(item)}
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ))}
                        </Stack>
                    )
                ) : pendingInvites.length === 0 && !isFetchingInvites ? (
                    <Box px={2} py={6}>
                        <Typography color="text.secondary" textAlign="center">
                            Nenhum convite pendente.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>E-mail</TableCell>
                                    <TableCell>Cargo</TableCell>
                                    <TableCell>Expira em</TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pendingInvites.map(invite => (
                                    <TableRow key={invite.id} hover>
                                        <TableCell>{invite.email}</TableCell>
                                        <TableCell>
                                            {invite.teamLabel || adminTeamLabel(invite.team)}
                                        </TableCell>
                                        <TableCell>
                                            {invite.expiresAt
                                                ? new Date(invite.expiresAt).toLocaleString('pt-BR')
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                title="Copiar link"
                                                onClick={() => handleCopyInviteLink(invite)}
                                            >
                                                <ContentCopy fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                title="Reenviar"
                                                onClick={() => handleResendInvite(invite)}
                                            >
                                                <Refresh fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                title="Revogar"
                                                onClick={() => setRevokeInvite(invite)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </ListPageLayout>
            <DialogInviteUser
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onSuccess={mutateInvites}
            />
            <DialogChangeUserTeam
                open={Boolean(editUser)}
                user={editUser}
                onClose={() => setEditUser(null)}
                onSuccess={mutate}
            />
            <ConfirmationModal
                open={Boolean(revokeInvite)}
                onClose={() => setRevokeInvite(null)}
                onConfirm={handleRevokeConfirm}
                title="Revogar convite"
                message={revokeInvite ? `Revogar o convite para ${revokeInvite.email}?` : ''}
            />
            <ConfirmationModal
                open={Boolean(deleteUser)}
                onClose={() => setDeleteUser(null)}
                onConfirm={handleDeleteConfirm}
                title="Excluir usuário"
                message={
                    deleteUser ? `Excluir ${deleteUser.userName} da equipe Eurobem?` : ''
                }
            />
        </>
    );
}
